import { useState, useEffect, useCallback } from 'react';
import { useAdminApi } from '../../Admin/hooks/useAdminApi';
import api from '../../../../services/api';
import apiV2 from '../../../../services/apiV2';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import { AdminCourse, isCourseActive } from '../../../../models/CourseModels';

export interface CourseSeason {
  seasonid: string;
  title: string;
  description?: string;
  position?: number;
}

export interface CourseMovie {
  courseid_seasonid_movieid?: string;
  movieid: string;
  title: string;
  position?: number;
  description?: string;
  url?: string;
  thumb?: string;
  videoduration?: string;
  m3u8?: string;
  url_vimeo_backup?: string;
  videotype?: string;
  exerciseshortmessage?: string;
  filter?: string;
  urlvertical?: string;
  isfree?: boolean;
}

export function useCourseData(initialCourseId = '') {
  const { fetchCourses } = useAdminApi();
  const { addToast } = useToast();
  const { user } = useAuth();

  // Sem `userid`, a API principal responde 500 e a V2 apaga `url`/`m3u8` das
  // aulas de cursos pagos (proteção da prévia do catálogo). A tela do aluno
  // manda o `userid`; o admin passa a mandar também.
  const userQuery = user?.userid ? `&userid=${user.userid}` : '';

  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [seasons, setSeasons] = useState<CourseSeason[]>([]);
  const [movies, setMovies] = useState<CourseMovie[]>([]);

  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [loadingMovies, setLoadingMovies] = useState(false);

  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [selectedMovieId, setSelectedMovieId] = useState('');

  // 1. Carregar lista de cursos do cliente atual (schoolid)
  useEffect(() => {
    async function loadCourses() {
      setLoadingCourses(true);
      try {
        const response = await fetchCourses(0);
        let list: AdminCourse[] = [];
        if (response?.data?.courses && Array.isArray(response.data.courses)) {
          list = response.data.courses;
        } else if (Array.isArray(response?.data)) {
          list = response.data as any;
        }

        if (list.length === 0) {
          // Fallback para API V2 ou V1
          const sub =
            process.env.NODE_ENV === 'development'
              ? `${process.env.REACT_APP_WHITELABEL_ADMIN}`
              : window.location.href.split('.')[0].replace('https://', '').replace('http://', '');
          const fallbackRes = await api.get<any>(`/course?schoolid=${sub}`);
          if (Array.isArray(fallbackRes?.data)) {
            list = fallbackRes.data;
          } else if (fallbackRes?.data?.courses) {
            list = fallbackRes.data.courses;
          }
        }

        setCourses(list.filter(isCourseActive));
      } catch (err) {
        console.error('Erro ao carregar cursos:', err);
        addToast({
          type: 'error',
          title: 'Erro ao carregar cursos',
          description: 'Não foi possível buscar a lista de cursos do cliente.',
        });
      } finally {
        setLoadingCourses(false);
      }
    }
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Carregar Seasons (Módulos) ao selecionar um Curso
  const handleCourseChange = useCallback(async (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedSeasonId('');
    setSelectedMovieId('');
    setSeasons([]);
    setMovies([]);

    if (!courseId) return;

    setLoadingSeasons(true);
    try {
      let seasonsData: any[] = [];
      try {
        const response = await api.get<any>(`/course/season?courseid=${courseId}${userQuery}`);
        seasonsData = Array.isArray(response.data) ? response.data : response.data?.seasons || [];
      } catch (err1) {
        const response2 = await apiV2.get<any>(`/course/season?courseid=${courseId}${userQuery}`);
        seasonsData = Array.isArray(response2.data) ? response2.data : response2.data?.seasons || [];
      }

      setSeasons(seasonsData);
    } catch (err) {
      console.error('Erro ao carregar módulos:', err);
      addToast({
        type: 'error',
        title: 'Erro ao carregar módulos',
        description: 'Não foi possível buscar as temporadas deste curso.',
      });
    } finally {
      setLoadingSeasons(false);
    }
  }, [addToast, userQuery]);

  // 3. Carregar Movies (Aulas) ao selecionar uma Season
  const handleSeasonChange = useCallback(async (seasonId: string, currentCourseId?: string) => {
    const activeCourseId = currentCourseId || selectedCourseId;
    setSelectedSeasonId(seasonId);
    setSelectedMovieId('');
    setMovies([]);

    if (!activeCourseId || !seasonId) return [];

    setLoadingMovies(true);
    let moviesData: CourseMovie[] = [];

    try {
      // 1º a rota do backoffice (devolve o item cru, com link, para o admin).
      // Enquanto ela não estiver publicada, falha e cai na API do aluno, que
      // esconde o link de curso pago para quem não comprou.
      try {
        const res = await apiBackoffice.get<any>(
          `/backoffice/movie?courseid=${activeCourseId}&seasonid=${seasonId}` +
            `&schoolid=${user?.schoolid ?? ''}${userQuery}`,
        );
        if (Array.isArray(res.data)) {
          setMovies(res.data);
          return res.data as CourseMovie[];
        }
      } catch {
        // Rota indisponível: segue para a API do aluno.
      }

      try {
        const res = await api.get<any>(
          `/course/season/movie?courseid=${activeCourseId}&seasonid=${seasonId}${userQuery}`,
        );
        if (Array.isArray(res.data)) moviesData = res.data;
        else if (Array.isArray(res.data?.movies)) moviesData = res.data.movies;
      } catch {
        const res2 = await apiV2.get<any>(
          `/course/season/movie?courseid=${activeCourseId}&seasonid=${seasonId}${userQuery}`,
        );
        if (Array.isArray(res2.data)) moviesData = res2.data;
        else if (Array.isArray(res2.data?.movies)) moviesData = res2.data.movies;
      }

      setMovies(moviesData);
      return moviesData;
    } catch (err) {
      console.error('Erro ao carregar aulas:', err);
      return [];
    } finally {
      setLoadingMovies(false);
    }
  }, [selectedCourseId, userQuery, user?.schoolid]);

  const handleMovieChange = useCallback((movieId: string) => {
    setSelectedMovieId(movieId);
  }, []);

  // Curso vindo da URL (?courseid=): carrega os módulos automaticamente.
  useEffect(() => {
    if (initialCourseId) {
      handleCourseChange(initialCourseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCourseId]);

  return {
    courses,
    seasons,
    movies,
    loadingCourses,
    loadingSeasons,
    loadingMovies,
    selectedCourseId,
    selectedSeasonId,
    selectedMovieId,
    setSelectedCourseId,
    setSelectedSeasonId,
    setSelectedMovieId,
    handleCourseChange,
    handleSeasonChange,
    handleMovieChange,
  };
}
